import { skipToken, useMutation, useQuery } from '@tanstack/react-query'

import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query'
import type { IClusterFormTemplatePayload } from '~/api/clusters'
import type {
  IAddToClusterResponse,
  IBatchAddToClusterPayload,
  IBatchAddToClusterResponse,
  IBatchDeleteFromClusterPayload,
  IClusterApp,
  TClusterForm,
  ICluster,
  ICreateClusterPayload,
  ICreateClusterResponse,
  IGetClusterCandidatesParams,
  IGetClusterResponse,
  IListAppsForClusterPayload,
  IListClustersParams,
  ISetTagsResponse,
  IUpdateClusterPayload,
  IGetClusterFormPayload,
  IBatchAddTagsPayload,
  TGetGlobalStatsResponse,
  TSetVideosPayload,
  IClustersAllForms,
  IClusterFormUpdatePayload,
  IClusterFormPayload,
  IRecommendedClusterList,
  TClusterFormTemplate,
  IAppCompetitor,
  IGetAppCompetitorsPayload,
} from '~/types/cluster'
import type { DeviceType, MobileDeviceType } from '~/types/device-type'
import type { ICompanyList, TCompaniesListParams } from '~/types/ranking'

import {
  addInfosToCluster,
  addToClusterUsingUrl,
  batchAddTags,
  batchAddToCluster,
  batchDeleteFromCluster,
  createCluster,
  deleteCluster,
  getAllForms,
  getClusterApp,
  getClusterCandidates,
  getClusterFeatures,
  getClusterForm,
  getClustersTags,
  getAppCompetitors,
  getClusterStats,
  getCluster,
  getCompaniesList,
  getFormTemplate,
  getGlobalStats,
  getRecommendedClustersList,
  getTags,
  listAppsForCluster,
  listClusters,
  partialSaveInfosForm,
  saveClusterFeatures,
  saveFormTemplate,
  setAppVideos,
  setClusterTags,
  setRecommendedCluster,
  setTags,
  updateCluster,
  allClusters,
} from '~/api/clusters'

////////// KEYS //////////
export const keys = {
  all: ['clusters'] as const,
  allForms: (params?: IClusterFormPayload) => [...keys.all, 'all-forms', params] as const,
  appCompetitors: (params: IGetAppCompetitorsPayload) => [...keys.all, 'app-competitors', params] as const,
  clusterAppDetails: (clusterId?: string, deviceType?: DeviceType, storeId?: string) =>
    [...keys.all, 'app-details', clusterId, deviceType, storeId] as const,
  clusterCandidates: (clusterId: string, params?: IGetClusterCandidatesParams) =>
    [...keys.all, 'cluster-candidates', clusterId, params] as const,
  clusterForm: (categoryId?: string, clusterId?: string, deviceType?: string, storeId?: string) =>
    [...keys.all, 'form', categoryId, clusterId, deviceType, storeId] as const,
  clusterStats: (clusterId?: string) => [...keys.all, 'cluster-stats', clusterId] as const,
  clustersTags: () => [...keys.all, 'tags'] as const,
  companiesList: (params?: TCompaniesListParams) => [...keys.all, 'companies-list', params] as const,
  details: (clusterId?: string) => [...keys.all, 'details', clusterId] as const,
  features: (clusterId?: string) => [...keys.all, 'features', clusterId] as const,
  formTemplate: (categoryId?: string) => [...keys.all, 'form-template', categoryId] as const,
  globalStats: () => [...keys.all, 'global-stats'] as const,
  listAppsForClusters: (params?: IListAppsForClusterPayload) =>
    [...keys.all, 'list-apps-for-clusters', params] as const,
  listClusters: (params?: IListClustersParams) => [...keys.all, 'list-clusters', params] as const,
  listLight: () => [...keys.all, 'list', 'light'] as const,
  recommendedClusterList: (params?: TCompaniesListParams) =>
    [...keys.all, 'recommended-clusters-list', params] as const,
  tags: (clusterId?: string) => [...keys.all, 'cluster-tags', clusterId] as const,
}

////////// QUERIES //////////

export const useClusterFormQuery = ({
  categoryId,
  clusterId,
  deviceType,
  storeId,
}: IGetClusterFormPayload): UseQueryResult<TClusterForm[], unknown> => {
  return useQuery({
    queryFn:
      categoryId && clusterId && deviceType && storeId
        ? () => getClusterForm({ categoryId, clusterId, deviceType, storeId })
        : skipToken,
    queryKey: keys.clusterForm(categoryId, clusterId, deviceType, storeId),
  })
}

export const useClusterStatsQuery = (clusterId?: string): UseQueryResult<ICluster, unknown> => {
  return useQuery({
    queryFn: clusterId ? () => getClusterStats(clusterId) : skipToken,
    queryKey: keys.clusterStats(clusterId),
  })
}

export const useGlobalStatsQuery = (): UseQueryResult<TGetGlobalStatsResponse> => {
  return useQuery({
    queryFn: getGlobalStats,
    queryKey: keys.globalStats(),
  })
}

export const useGetAppCompetitorsQuery = (params: IGetAppCompetitorsPayload): UseQueryResult<IAppCompetitor[]> => {
  return useQuery({
    queryFn: params.storeId ? () => getAppCompetitors(params) : skipToken,
    queryKey: keys.appCompetitors(params),
  })
}

export const useGetClusterQuery = (clusterId?: string): UseQueryResult<IGetClusterResponse> => {
  return useQuery({
    queryFn: clusterId ? () => getCluster(clusterId) : skipToken,
    queryKey: keys.details(clusterId),
  })
}

export const useClustersLightListQuery = (): UseQueryResult<{ cluster_id: string; name: string }[]> => {
  return useQuery({
    queryFn: allClusters,
    queryKey: keys.listLight(),
  })
}

export const useClustersListQuery = (params?: IListClustersParams): UseQueryResult<IPaginatedResponse<ICluster>> => {
  return useQuery({
    queryFn: () => listClusters(params),
    queryKey: keys.listClusters(params),
  })
}

export const useListAppsForClusterQuery = (
  params: IListAppsForClusterPayload
): UseQueryResult<IPaginatedResponse<IClusterApp>> => {
  return useQuery({
    queryFn: params.clusterId ? () => listAppsForCluster(params) : skipToken,
    queryKey: keys.listAppsForClusters(params),
  })
}

export const useGetAppFromClusterQuery = (
  clusterId?: string,
  deviceType?: DeviceType,
  storeId?: string
): UseQueryResult<IClusterApp> => {
  return useQuery({
    queryFn: clusterId && deviceType && storeId ? () => getClusterApp(clusterId, deviceType, storeId) : skipToken,
    queryKey: keys.clusterAppDetails(clusterId, deviceType, storeId),
  })
}

export const useGetClusterCandidatesQuery = (
  clusterId: string,
  params?: IGetClusterCandidatesParams,
  select?: ((data: IPaginatedResponse<IClusterApp>) => IPaginatedResponse<IClusterApp>) | undefined
): UseQueryResult<IPaginatedResponse<IClusterApp>> => {
  return useQuery({
    queryFn: () => getClusterCandidates(clusterId, params),
    queryKey: keys.clusterCandidates(clusterId, params),
    select: (response) => (select ? select(response) : response),
  })
}

export const useCompaniesListQuery = (
  params?: TCompaniesListParams
): UseQueryResult<IPaginatedResponse<ICompanyList>> => {
  return useQuery({
    queryFn: () => getCompaniesList(params),
    queryKey: keys.companiesList(params),
  })
}

export const useRecommendedClustersListQuery = (
  params?: TCompaniesListParams
): UseQueryResult<IPaginatedResponse<IRecommendedClusterList>> => {
  return useQuery({
    queryFn: () => getRecommendedClustersList(params),
    queryKey: keys.recommendedClusterList(params),
  })
}
export const useClustersTagsQuery = (): UseQueryResult<string[]> => {
  return useQuery({
    queryFn: getClustersTags,
    queryKey: keys.clustersTags(),
  })
}

export const useTagsQuery = (clusterId?: string): UseQueryResult<string[]> => {
  return useQuery({
    queryFn: clusterId ? () => getTags(clusterId) : skipToken,
    queryKey: keys.tags(clusterId),
  })
}

export const useClustersAllFormsQuery = (params: IClusterFormPayload): UseQueryResult<IClustersAllForms> => {
  return useQuery({
    queryFn: params ? () => getAllForms(params) : skipToken,
    queryKey: keys.allForms(params),
  })
}

export const useFormTemplateQuery = (categoryId?: string): UseQueryResult<TClusterFormTemplate[]> => {
  return useQuery({
    queryFn: categoryId ? () => getFormTemplate(categoryId) : skipToken,
    queryKey: keys.formTemplate(categoryId),
  })
}

export const useClusterFeaturesQuery = (clusterId?: string): UseQueryResult<TClusterFormTemplate[]> => {
  return useQuery({
    queryFn: clusterId ? () => getClusterFeatures(clusterId) : skipToken,
    queryKey: keys.features(clusterId),
  })
}

////////// MUTATIONS //////////
export const useCreateClusterMutation = (): UseMutationResult<
  ICreateClusterResponse,
  unknown,
  ICreateClusterPayload,
  unknown
> => {
  return useMutation({ mutationFn: createCluster })
}

export const useAddToClusterUsingUrlMutation = (): UseMutationResult<
  IAddToClusterResponse,
  unknown,
  { clusterId: string; storeUrl: string },
  unknown
> => {
  return useMutation({ mutationFn: addToClusterUsingUrl })
}

export const useBatchAddToClusterMutation = (): UseMutationResult<
  IBatchAddToClusterResponse,
  unknown,
  IBatchAddToClusterPayload,
  unknown
> => {
  return useMutation({ mutationFn: batchAddToCluster })
}

export const useBatchDeleteFromClusterMutation = (): UseMutationResult<
  void,
  unknown,
  IBatchDeleteFromClusterPayload,
  unknown
> => {
  return useMutation({ mutationFn: batchDeleteFromCluster })
}

export const useUpdateClusterMutation = (): UseMutationResult<void, unknown, IUpdateClusterPayload, unknown> => {
  return useMutation({ mutationFn: updateCluster })
}

export const useAddInfosToClusterMutation = (): UseMutationResult<
  void,
  unknown,
  { categoryId: string; clusterId: string; deviceType: string; fields: any; storeId: string },
  unknown
> => {
  return useMutation({ mutationFn: addInfosToCluster })
}

export const useDeleteClusterMutation = (): UseMutationResult<void, unknown, { clusterId: string }, unknown> => {
  return useMutation({ mutationFn: deleteCluster })
}

export const useSetTagsMutation = (): UseMutationResult<
  ISetTagsResponse,
  unknown,
  {
    clusterId: string
    deviceType: string
    storeId: string
    tags: string[]
  },
  unknown
> => {
  return useMutation({ mutationFn: setTags })
}

export const useBatchAddTagsMutation = (): UseMutationResult<void, unknown, IBatchAddTagsPayload, unknown> => {
  return useMutation({ mutationFn: batchAddTags })
}

export const useSetClusterTagsMutation = (): UseMutationResult<
  void,
  unknown,
  { clusterId: string; tags: string[] },
  unknown
> => {
  return useMutation({ mutationFn: setClusterTags })
}

export const useSetAppVideosMutation = (): UseMutationResult<void, unknown, TSetVideosPayload, unknown> => {
  return useMutation({ mutationFn: setAppVideos })
}

export const usePartialSaveInfosMutation = (): UseMutationResult<void, unknown, IClusterFormUpdatePayload, unknown> => {
  return useMutation({ mutationFn: partialSaveInfosForm })
}

export const useSaveClusterFeaturesMutation = (): UseMutationResult<
  void,
  unknown,
  { clusterId: string; data: IClusterFormTemplatePayload[] },
  unknown
> => {
  return useMutation({ mutationFn: saveClusterFeatures })
}

export const useRecommendedClusterMutation = (): UseMutationResult<
  void,
  unknown,
  { clusterId?: string; deviceType: MobileDeviceType; status: string; storeId: string },
  unknown
> => {
  return useMutation({ mutationFn: setRecommendedCluster })
}

export const useSaveFormTemplateMutation = (): UseMutationResult<
  void,
  unknown,
  { categoryId: string; data: TClusterFormTemplate[] },
  unknown
> => {
  return useMutation({ mutationFn: saveFormTemplate })
}
